import React, { useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Trash, UserCircle, Buildings } from "@phosphor-icons/react";
import WhyThisMatters from "../WhyThisMatters";
import { track } from "../../lib/analytics";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

const Field = ({ label, children, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    <Label className="label-tiny">{label}</Label>
    {children}
  </div>
);

const inputCls = "rounded-none border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900";

export default function IntakeTab({ caseData, save }) {
  const [intake, setIntake] = useState({
    case_title: "",
    jurisdiction: "Federal",
    court_name: "",
    court_state: "",
    case_number: "",
    ...(caseData.intake || {}),
  });
  const [parties, setParties] = useState(caseData.parties || []);

  const addParty = (role) => {
    setParties([...parties, { id: crypto.randomUUID(), role, name: "", address: "", description: "" }]);
  };
  const updateParty = (id, key, val) => {
    setParties(parties.map((p) => (p.id === id ? { ...p, [key]: val } : p)));
  };
  const removeParty = (id) => setParties(parties.filter((p) => p.id !== id));

  const handleSave = () => {
    track("intake_saved", { case_id: caseData.case_id, parties_count: parties.length });
    save({ intake, parties });
  };

  return (
    <div className="space-y-10">
      <section>
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-serif-display text-3xl text-slate-900">Case Intake</h2>
          <span className="label-tiny">Step 1 of 8</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Case Title (Caption)" testid="field-case-title">
            <Input data-testid="intake-case-title" value={intake.case_title} onChange={(e) => setIntake({ ...intake, case_title: e.target.value })} placeholder="Doe v. Acme Corp." className={inputCls} />
          </Field>
          <Field label="Case / Docket Number (optional)" testid="field-case-number">
            <Input data-testid="intake-case-number" value={intake.case_number} onChange={(e) => setIntake({ ...intake, case_number: e.target.value })} placeholder="To be assigned" className={inputCls} />
          </Field>
          <Field label="Jurisdiction" testid="field-jurisdiction">
            <Select value={intake.jurisdiction} onValueChange={(v) => setIntake({ ...intake, jurisdiction: v })}>
              <SelectTrigger className={inputCls} data-testid="intake-jurisdiction"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Federal">Federal</SelectItem>
                <SelectItem value="State">State</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Court Name" testid="field-court-name">
            <Input data-testid="intake-court-name" value={intake.court_name} onChange={(e) => setIntake({ ...intake, court_name: e.target.value })} placeholder="United States District Court" className={inputCls} />
          </Field>
          <Field label="Court State / District" testid="field-court-state">
            <Select value={intake.court_state} onValueChange={(v) => setIntake({ ...intake, court_state: v })}>
              <SelectTrigger className={inputCls} data-testid="intake-court-state"><SelectValue placeholder="Select state" /></SelectTrigger>
              <SelectContent>
                {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </section>

      <section>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-serif-display text-2xl text-slate-900">Parties</h2>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => addParty("plaintiff")} data-testid="add-plaintiff-btn" className="rounded-none border-slate-300">
              <Plus className="w-4 h-4 mr-1" /> Plaintiff
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => addParty("defendant")} data-testid="add-defendant-btn" className="rounded-none border-slate-300">
              <Plus className="w-4 h-4 mr-1" /> Defendant
            </Button>
          </div>
        </div>

        {parties.length === 0 && (
          <div className="border border-dashed border-slate-300 p-8 text-center text-slate-500">
            No parties yet. Add at least one plaintiff and one defendant.
          </div>
        )}

        <div className="space-y-4">
          {parties.map((p) => (
            <div key={p.id} className="border border-slate-200 p-5" data-testid={`party-card-${p.id}`}>
              <div className="flex items-center gap-3 mb-4">
                <UserCircle weight="duotone" className="w-6 h-6 text-slate-700" />
                <span className="label-tiny">{p.role}</span>
                <Button type="button" variant="ghost" size="sm" onClick={() => removeParty(p.id)} className="ml-auto text-red-700 hover:bg-red-50 rounded-none" data-testid={`remove-party-${p.id}`}>
                  <Trash className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Input value={p.name} onChange={(e) => updateParty(p.id, "name", e.target.value)} placeholder="Name (full legal name)" className={inputCls} data-testid={`party-name-${p.id}`} />
                <Input value={p.address} onChange={(e) => updateParty(p.id, "address", e.target.value)} placeholder="Address" className={inputCls} data-testid={`party-address-${p.id}`} />
                <Textarea value={p.description} onChange={(e) => updateParty(p.id, "description", e.target.value)} placeholder="Description (e.g., 'an individual residing in...', 'a Delaware corporation with principal place of business in...')" className={`${inputCls} md:col-span-2 min-h-[70px]`} data-testid={`party-description-${p.id}`} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="flex justify-end pt-2 border-t border-slate-200 mt-8">
        <Button onClick={handleSave} data-testid="save-intake-btn" className="rounded-none bg-slate-900 hover:bg-slate-800 text-white">
          Save Intake
        </Button>
      </div>

      {/* Look up an entity — 50-state Business Search Directory */}
      <section className="border border-slate-200 p-6" data-testid="business-search-launcher">
        <div className="flex items-center gap-2 mb-2">
          <Buildings weight="duotone" className="w-5 h-5 text-slate-700" />
          <h3 className="font-serif-display text-xl text-slate-900">Look up a business entity</h3>
        </div>
        <p className="text-slate-700 text-sm leading-relaxed max-w-2xl">
          If your claim involves a company, confirm the correct legal name and registered agent
          before filing. Search official state business databases (50 states + D.C.), save what
          you find to this case, and import it into your Parties list when you are ready.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <a
            href={`/resources/business-search?case_id=${caseData.case_id}`}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 px-4 py-2"
            data-testid="open-business-search-btn"
          >
            Open business search
          </a>
          <span className="text-xs text-slate-500">
            Opens the Resource Center directory linked to this case.
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Business-search results may not establish which party is legally responsible or where
          legal documents must be served. Confirm important information with the official agency,
          court, or a licensed attorney.
        </p>
      </section>

      <WhyThisMatters step="intake" />
    </div>
  );
}
